import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { pln } from './format';
import { useTranslation } from 'react-i18next';

// Dymek „Proponowany produkt" obok sekcji kategorii (miejsce pod upsell dla firm —
// promowana oferta). Czysto prezentacyjny: dane przychodzą z api/recommend.js.
// product: { id, name, img_url, seller, price, availability } + specs [{label, value}]
// pinnable/pinned/onPin: checkbox „ten produkt w zestawie" (wymaga model_id oferty —
// silnik doboru pracuje na warstwie modeli, oferta bez modelu nie da się przypiąć).
const RecommendedProduct = ({ title: tytul, product, specs = [], link, bmsLine, bmsSpecLine, note, loading, pinnable = false, pinned = false, onPin }) => {
  const { t } = useTranslation('asystent');
  const title = tytul ?? t('polecany.domyslny_tytul');
  if (loading) {
    return (
      <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/70 to-white p-3.5 animate-pulse">
        <div className="h-4 w-28 bg-blue-100 rounded mb-3" />
        <div className="h-20 bg-blue-50 rounded-lg mb-2" />
        <div className="h-3.5 w-full bg-blue-50 rounded mb-1.5" />
        <div className="h-3.5 w-2/3 bg-blue-50 rounded" />
      </div>
    );
  }
  if (!product) return null;

  return (
    <aside aria-label={title} className={`rounded-xl border p-3.5 shadow-sm transition-colors ${
      pinned ? 'border-blue-500 ring-1 ring-blue-500/40 bg-gradient-to-br from-blue-100/80 to-white' : 'border-blue-200 bg-gradient-to-br from-blue-50/70 to-white'
    }`}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-blue-700">
          <FontAwesomeIcon icon={faStar} className="text-amber-400" />
          {title}
        </span>
        <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{t('polecany.polecane')}</span>
      </div>

      <Link to={link} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg">
        {product.img_url && (
          <div className="h-24 mb-2.5 flex items-center justify-center bg-white rounded-lg border border-gray-100 overflow-hidden">
            <img src={product.img_url} alt={product.name} loading="lazy" className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform duration-300" />
          </div>
        )}
        <p className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:text-blue-700 transition-colors">{product.name}</p>
      </Link>

      {specs.length > 0 && (
        <dl className="mt-2 space-y-1">
          {specs.filter((s) => s.value != null && s.value !== '').map((s) => (
            <div key={s.label} className="flex items-baseline justify-between gap-2 text-[11px]">
              <dt className="text-gray-500">{s.label}</dt>
              <dd className="font-medium text-gray-800 text-right">{s.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-2.5 pt-2 border-t border-blue-100 flex items-baseline justify-between gap-2">
        <span className="text-[11px] text-gray-500 truncate">{product.seller}</span>
        {product.price != null && <span className="text-sm font-bold text-gray-900 shrink-0">{pln(product.price)}</span>}
      </div>

      {bmsLine && (
        <p className="mt-1.5 text-[11px] text-gray-600 bg-white/70 border border-blue-100 rounded-lg px-2 py-1.5">
          {bmsLine}
          {/* Parametry sterownika z karty katalogowej. Sterownik nie ma własnej
              strony produktu, więc to JEDYNE miejsce, gdzie użytkownik je zobaczy. */}
          {bmsSpecLine && (
            <span className="block mt-1 text-[10px] text-gray-500">{bmsSpecLine}</span>
          )}
        </p>
      )}
      {note && <p className="mt-1.5 text-[11px] text-amber-700">{note}</p>}

      {pinnable && (
        <label className={`mt-2.5 flex items-center gap-2 rounded-lg border px-2.5 py-2 cursor-pointer select-none transition-colors ${
          pinned ? 'border-blue-500 bg-blue-600 text-white' : 'border-blue-200 bg-white/80 text-gray-700 hover:border-blue-400'
        }`}>
          <input type="checkbox" checked={pinned} onChange={(e) => onPin?.(e.target.checked)}
            className="h-3.5 w-3.5 rounded accent-blue-600" />
          <span className="text-[11px] font-semibold leading-tight">
            {pinned ? t('polecany.w_zestawie') : t('polecany.wybierz')}
          </span>
        </label>
      )}

      <Link to={link} className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-500">
        {t('polecany.zobacz')} <FontAwesomeIcon icon={faArrowRight} className="text-[9px]" />
      </Link>
    </aside>
  );
};

export default React.memo(RecommendedProduct);
